import { useCallback, useEffect, useRef, useState } from "react";

// Native, single-shot Turkish speech-to-text.
//
// This hook is deliberately thin: it wraps `SpeechRecognition` /
// `webkitSpeechRecognition` with the exact behaviour approved for Voice V1.
//
//   * Single-shot: `continuous=false`, so the browser stops on end-of-speech.
//   * `interimResults=true` so the UI can show a live-updating preview.
//   * `lang="tr-TR"` — Turkish only, matches product scope.
//   * Never auto-sends. The caller decides when to hand the transcript
//     off to the existing chat composer.
//   * No network fallback. If the browser exposes no SpeechRecognition
//     surface we simply report `supported: false` and the UI shows a
//     disabled state; a Whisper server fallback lands in C2.
//
// Governance: the hook returns *nothing* about the underlying vendor.
// The only user-facing strings live in the components that mount it.
export function useVoiceInput({ lang = "tr-TR" } = {}) {
  const RecognitionCtor =
    typeof window !== "undefined"
      ? window.SpeechRecognition || window.webkitSpeechRecognition
      : null;
  const supported = Boolean(RecognitionCtor);

  const recognitionRef = useRef(null);
  const finalRef = useRef("");

  // status: "idle" | "listening" | "denied" | "error" | "unsupported"
  const [status, setStatus] = useState(supported ? "idle" : "unsupported");
  const [interim, setInterim] = useState("");
  const [transcript, setTranscript] = useState("");
  const [error, setError] = useState(null);

  const cleanup = useCallback(() => {
    const r = recognitionRef.current;
    if (!r) return;
    try {
      r.onresult = null;
      r.onerror = null;
      r.onend = null;
      r.onstart = null;
    } catch (_) { /* noop */ }
  }, []);

  useEffect(() => {
    return () => {
      try { recognitionRef.current?.stop(); } catch (_) { /* noop */ }
      cleanup();
    };
  }, [cleanup]);

  const start = useCallback(() => {
    if (!supported) {
      setStatus("unsupported");
      return;
    }
    // Reset transcript so caller gets a clean single-shot result.
    finalRef.current = "";
    setInterim("");
    setTranscript("");
    setError(null);

    let recognition;
    try {
      recognition = new RecognitionCtor();
    } catch (e) {
      setStatus("error");
      setError("Ses tanıma başlatılamadı");
      return;
    }
    recognition.lang = lang;
    recognition.continuous = false;
    recognition.interimResults = true;
    recognition.maxAlternatives = 1;

    recognition.onstart = () => setStatus("listening");
    recognition.onresult = (event) => {
      let interimText = "";
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const res = event.results[i];
        const chunk = res[0]?.transcript || "";
        if (res.isFinal) {
          finalRef.current = (finalRef.current + " " + chunk).trim();
        } else {
          interimText += chunk;
        }
      }
      setInterim(interimText);
      if (finalRef.current) setTranscript(finalRef.current);
    };
    recognition.onerror = (event) => {
      // event.error is one of: not-allowed | service-not-allowed | no-speech
      // | aborted | audio-capture | network | bad-grammar | language-not-supported
      const code = event?.error || "unknown";
      if (code === "not-allowed" || code === "service-not-allowed") {
        setStatus("denied");
        setError("Mikrofon izni verilmedi");
      } else if (code === "no-speech") {
        setStatus("idle");
        setError("Ses algılanmadı, tekrar dene");
      } else if (code === "audio-capture") {
        setStatus("error");
        setError("Mikrofon bulunamadı");
      } else if (code === "aborted") {
        // user or code called stop() — quiet state change only
        setStatus("idle");
      } else {
        setStatus("error");
        setError("Ses tanıma sırasında bir sorun oluştu");
      }
    };
    recognition.onend = () => {
      setInterim("");
      setStatus((s) => (s === "listening" ? "idle" : s));
    };

    recognitionRef.current = recognition;
    try {
      recognition.start();
    } catch (e) {
      // Chrome throws if start() called while already running; treat as no-op.
      setStatus("error");
      setError("Ses tanıma başlatılamadı");
    }
  }, [RecognitionCtor, lang, supported]);

  const stop = useCallback(() => {
    try { recognitionRef.current?.stop(); } catch (_) { /* noop */ }
  }, []);

  const reset = useCallback(() => {
    finalRef.current = "";
    setInterim("");
    setTranscript("");
    setError(null);
    if (status !== "listening") setStatus(supported ? "idle" : "unsupported");
  }, [status, supported]);

  return {
    supported,
    status,        // "idle" | "listening" | "denied" | "error" | "unsupported"
    interim,       // live partial text (may be empty)
    transcript,    // final accumulated text
    error,         // Turkish error string or null
    start,
    stop,
    reset,
  };
}
